import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Divider, useTheme } from '@mui/material';
import { Groups as GroupsIcon, Code as CodeIcon } from '@mui/icons-material';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedPaper = styled(Paper)<{ isVisible: boolean }>`
  animation: ${({ isVisible }) => isVisible ? fadeInUp : 'none'} 1s ease-out forwards;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
`;

const StatsBanner = () => {
  const theme = useTheme();
  const statsRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  // --- Configuration ---
  // TODO: Replace this with a dynamic value from your API in the future
  const TARGET_USERS = 2500; 
  const TARGET_CODE_LINES = 13866;
  const ANIMATION_DURATION_MS = 2000; // 2 seconds

  const [userCount, setUserCount] = useState(0);
  const [codeCount, setCodeCount] = useState(0);

  useEffect(() => {
    const currentRef = statsRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          
          // Start counting animation when component becomes visible
          let startTime: number | undefined;
          const animate = (timestamp: DOMHighResTimeStamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const progressPercent = Math.min(progress / ANIMATION_DURATION_MS, 1);

            const currentUser = Math.floor(progressPercent * TARGET_USERS);
            const currentCode = Math.floor(progressPercent * TARGET_CODE_LINES);

            setUserCount(currentUser);
            setCodeCount(currentCode);

            if (progress < ANIMATION_DURATION_MS) {
              requestAnimationFrame(animate);
            } else {
              setUserCount(TARGET_USERS); // Ensure it ends on the exact number
              setCodeCount(TARGET_CODE_LINES);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 } // Trigger when 30% of the component is visible
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasAnimated]);

  interface StatItemProps {
    icon: React.ReactNode;
    value: number;
    label: string;
  }

  const StatItem = ({ icon, value, label }: StatItemProps) => (
    <Box sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
      {icon}
      <Typography 
        variant="h3" 
        component="p" 
        sx={{ 
          fontWeight: 700, 
          my: 1,
          color: 'primary.main',
        }}
      >
        {/* Adds commas to the number */}
        {value.toLocaleString()}+
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }} ref={statsRef}>
      <AnimatedPaper 
        elevation={3} 
        isVisible={isVisible}
        sx={{ 
          maxWidth: 800, 
          mx: 'auto', 
          borderRadius: 3,
          background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box 
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
            alignItems: 'center',
            justifyItems: 'center',
            gap: { xs: 2, sm: 4 }
          }}
        >
          <StatItem 
            icon={<GroupsIcon sx={{ fontSize: 40, color: 'secondary.main' }} />}
            value={userCount}
            label="Happy Users"
          />
          <Divider 
            orientation="vertical" 
            flexItem 
            sx={{ display: { xs: 'none', sm: 'block' }, height: '100px', my: 'auto' }}
          />
          <Divider sx={{ width: '80px', display: { xs: 'block', sm: 'none' }, my: 2 }} />
          <StatItem 
            icon={<CodeIcon sx={{ fontSize: 40, color: 'secondary.main' }} />}
            value={codeCount}
            label="Lines of Code"
          />
        </Box>
      </AnimatedPaper>
    </Box>
  );
};

export default StatsBanner;
